/**
 * Label Filter Provider
 *
 * Provides label filter state to all children components.
 * Uses URL params for state persistence across routes.
 */

import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { LabelFilterContext } from './LabelFilterContextDef'
import { useLabelFilterState } from '../hooks/useLabelFilterState'
import type { LabelFilterContextValue } from '../types'

interface LabelFilterProviderProps {
  children: React.ReactNode
  /** Override mindmap ID (useful for testing or non-route contexts) */
  mindmapId?: string
  /** Whether to sync to URL (default: true) */
  syncToUrl?: boolean
}

/**
 * Provider component for label filter state.
 *
 * @example
 * ```tsx
 * // In a route component
 * <LabelFilterProvider>
 *   <MindMapFlowContent />
 * </LabelFilterProvider>
 *
 * // With explicit mindmapId
 * <LabelFilterProvider mindmapId={id}>
 *   <ReportContent />
 * </LabelFilterProvider>
 * ```
 */
export function LabelFilterProvider({
  children,
  mindmapId: explicitMindmapId,
  syncToUrl = true,
}: LabelFilterProviderProps) {
  // Get mindmapId from URL params if not explicitly provided
  const { id: routeMindmapId } = useParams<{ id: string }>()
  const mindmapId = explicitMindmapId ?? routeMindmapId

  const {
    selectedFilterLabels,
    setSelectedFilterLabels,
    clearFilterLabels,
    toggleFilterLabel,
    hasActiveFilter,
  } = useLabelFilterState({ mindmapId, syncToUrl })

  const value = useMemo<LabelFilterContextValue>(
    () => ({
      selectedFilterLabels,
      setSelectedFilterLabels,
      clearFilterLabels,
      toggleFilterLabel,
      hasActiveFilter,
    }),
    [
      selectedFilterLabels,
      setSelectedFilterLabels,
      clearFilterLabels,
      toggleFilterLabel,
      hasActiveFilter,
    ]
  )

  return <LabelFilterContext.Provider value={value}>{children}</LabelFilterContext.Provider>
}
